import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { User as UserIcon } from "lucide-react";
import { UserProfile } from "../components/profile/UserProfile";
import { AppSettings } from "../components/profile/AppSettings";
import { SubscriptionInfo } from "../components/profile/SubscriptionInfo";
import { UserDetails } from "../components/profile/UserDetails";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../hooks/useProfile";

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth(); // Usamos setUser del AuthContext
  const { updateProfile, isLoading, error } = useProfile();

  // UserProfile states
  const [bio, setBio] = useState(user?.bio || '');
  const [expectations, setExpectations] = useState(user?.expectations || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Add local draft states to track unsaved changes
  const [bioDraft, setBioDraft] = useState(user?.bio || '');
  const [expectationsDraft, setExpectationsDraft] = useState(user?.expectations || '');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // UserDetails states and handlers
  const [emailVerified, setEmailVerified] = useState(user?.emailVerified || false);
  const [userDetails, setUserDetails] = useState({
    name: user?.name || '',
    email: user?.email || '',
    createdAt: user?.createdAt || null,
    isPremium: user?.isPremium || false
  });

  const prevUserRef = useRef(user);

  // Function to handle email verification
  const handleVerifyEmail = async () => {
    try {
      setIsSaving(true);
      // Implementación real de la verificación de email
      // Ejemplo: await authAPI.sendVerificationEmail(user.email);

      // Simulación para este ejemplo
      const result = { success: true };

      if (result.success) {
        // Actualizar UI para mostrar éxito
        // No actualizamos emailVerified aquí porque el usuario aún necesita hacer clic en el enlace del email
      }
    } catch (err) {
      console.error("Failed to send verification email:", err);
    } finally {
      setIsSaving(false);
    }
  };

  // Initialize state on first render and when user changes
  useEffect(() => {
    // Skip if no user
    if (!user) return;

    // Initial state setup or complete user object change
    if (prevUserRef.current === undefined || user?.id !== prevUserRef.current?.id) {
      // Complete initialization of all states
      setBio(user.bio || '');
      setBioDraft(user.bio || '');
      setExpectations(user.expectations || '');
      setExpectationsDraft(user.expectations || '');
      setEmailVerified(user.emailVerified || false);
      setUserDetails({
        name: user.name || '',
        email: user.email || '',
        createdAt: user.createdAt || null,
        isPremium: user.isPremium || false
      });
      setHasUnsavedChanges(false);
    } else {
      // Selective update for fields that changed in the server
      // Only update non-edited fields (bio and expectations are updated only via save button)
      if (user.bio !== prevUserRef.current?.bio) {
        // Only update if there are no unsaved changes
        if (bio === bioDraft) {
          setBio(user.bio || '');
          setBioDraft(user.bio || '');
        }
      }

      if (user.expectations !== prevUserRef.current?.expectations) {
        // Only update if there are no unsaved changes
        if (expectations === expectationsDraft) {
          setExpectations(user.expectations || '');
          setExpectationsDraft(user.expectations || '');
        }
      }

      // Always update UserDetails states since they're not directly editable
      if (user.emailVerified !== prevUserRef.current?.emailVerified) {
        setEmailVerified(user.emailVerified || false);
      }

      // Update user details object only if relevant fields changed
      const detailsChanged =
        user.name !== prevUserRef.current?.name ||
        user.email !== prevUserRef.current?.email ||
        user.createdAt !== prevUserRef.current?.createdAt ||
        user.isPremium !== prevUserRef.current?.isPremium;

      if (detailsChanged) {
        setUserDetails({
          name: user.name || '',
          email: user.email || '',
          createdAt: user.createdAt || null,
          isPremium: user.isPremium || false
        });
      }
    }

    // Update reference for next comparison
    prevUserRef.current = user;
  }, [user, bio, expectations, bioDraft, expectationsDraft]);

  // Check for unsaved changes whenever drafts change
  useEffect(() => {
    setHasUnsavedChanges(
      bioDraft !== bio ||
      expectationsDraft !== expectations
    );
  }, [bioDraft, expectationsDraft, bio, expectations]);

  // Handle draft changes
  const handleBioDraftChange = (value) => {
    setBioDraft(value);
  };

  const handleExpectationsDraftChange = (value) => {
    setExpectationsDraft(value);
  };

  // Save changes function (called by the save button)
  const handleSaveChanges = async () => {
    if (!hasUnsavedChanges || !user) return;

    const updateData = {};

    // Only include fields that actually changed
    if (bioDraft !== bio) {
      updateData.bio = bioDraft;
    }

    if (expectationsDraft !== expectations) {
      updateData.expectations = expectationsDraft;
    }

    // Only proceed if we have changes to save
    if (Object.keys(updateData).length === 0) return;

    try {
      setIsSaving(true);

      // Actualizar en el servidor
      const response = await updateProfile(updateData);

      // Actualizar el estado local
      if (updateData.bio !== undefined) {
        setBio(bioDraft);
      }

      if (updateData.expectations !== undefined) {
        setExpectations(expectationsDraft);
      }

      // IMPORTANTE: Actualizar el contexto de autenticación
      // Esto garantiza que los datos persistan incluso cuando el componente se vuelva a renderizar
      if (user && setUser) {
        // Creamos un nuevo objeto de usuario con los datos actualizados
        const updatedUser = {
          ...user,
          ...updateData
        };

        // Actualizar el contexto de autenticación con los nuevos datos
        // Esto también actualizará el almacenamiento local gracias a tu implementación
        setUser(updatedUser);
      }

      // Show success indicator briefly
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      console.error(t('userProfile.error'), err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 mb-20">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold flex items-center mb-6 text-gray-900 dark:text-white">
          <UserIcon className="w-6 h-6 mr-2 text-blue-600 dark:text-blue-400" />
          {t('profile.title')}
        </h2>

        <div className="space-y-8">
          <UserDetails
            userDetails={userDetails}
            emailVerified={emailVerified}
            onVerifyEmail={handleVerifyEmail}
          />
          <UserProfile
            bio={bioDraft}
            expectations={expectationsDraft}
            isLoading={isLoading}
            isSaving={isSaving}
            saveSuccess={saveSuccess}
            error={error}
            hasUnsavedChanges={hasUnsavedChanges}
            onBioChange={handleBioDraftChange}
            onExpectationsChange={handleExpectationsDraftChange}
            onSave={handleSaveChanges}
          />
          <AppSettings />
          <SubscriptionInfo />
        </div>
      </div>
    </div>
  );
}